import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import "bootstrap-daterangepicker/daterangepicker.css";
import Chart from "react-apexcharts";
import ReactApexChart from "react-apexcharts";
import { Doughnut } from "react-chartjs-2";
import ApexCharts from "react-apexcharts";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { all_routes } from "../../Router/all_routes";
import PredefinedDateRanges from "../../core/common/range-picker/datePicker";
import {
  getTotalProducts,
  getTotalStock,
  getTotalOutOfStock,
  getSalesInvoiceTotals,
  getTotalSuppliers,
  getTotalCustomers,
  getTotalOrders,
  getCustomersOverview,
  getTopSellingProducts,
  getLowStock,
  getRecentSales,
  getSalesStats,
  getRecentTransactions,
  getTopCustomers,
  getTopCategories,
  getOrderStatistics,
} from "../../api/dashboard.js";

import { isDemoActive, getDemoReason } from "../../api/client";
import { mockDashboard } from "../../api/mock/dashboard";

// Register the necessary components for Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// Helper: detect if current language is RTL
const isRTL = () => document.documentElement.dir === "rtl";

// Reusable dropdown item style for LTR/RTL
const getDropdownItemStyle = () => ({
  display: "flex",
  alignItems: "center",
  flexDirection: isRTL() ? "row-reverse" : "row",
  textAlign: isRTL() ? "right" : "left",
});

const DASHBOARD_TRANSLATIONS = {
  ar: {
    "Welcome Admin": "مرحباً أيها المدير",
    "Here is your inventory, stock, and sales performance overview.":
      "هنا نظرة عامة على المخزون والمنتجات وأداء المبيعات.",
    "Inventory Dashboard": "لوحة تحكم المخزون",
    "Live overview": "نظرة مباشرة",
    TotalProducts: "إجمالي المنتجات",
    "Total Products": "إجمالي المنتجات",
    "Total Stock": "إجمالي المخزون",
    "Total Out of Stock": "إجمالي نفاد المخزون",
    "Total sales invoce": "إجمالي فواتير المبيعات",
    "Total sales invoice": "إجمالي فواتير المبيعات",
    "Overall Information": "معلومات عامة",
    Suppliers: "الموردون",
    Customers: "العملاء",
    Orders: "الطلبات",
    "Customers Overview": "نظرة عامة على العملاء",
    "First Time": "عملاء جدد",
    Return: "عائدون",
    "Top Selling Products": "المنتجات الأكثر مبيعاً",
    "Low Stock Products": "منتجات منخفضة المخزون",
    "Recent Sales": "المبيعات الأخيرة",
    "Sales Statics": "إحصائيات المبيعات",
    "Sales Statistics": "إحصائيات المبيعات",
    "Recent Transactions": "المعاملات الأخيرة",
    "Top Customers": "أفضل العملاء",
    "Top Categories": "أفضل الفئات",
    "Order Statistics": "إحصائيات الطلبات",
    Today: "اليوم",
    Weekly: "أسبوعي",
    Monthly: "شهري",
    "View All": "عرض الكل",
    Sales: "مبيعات",
    SKU: "رمز المنتج",
    Instock: "متوفر",
    "No low stock items": "لا توجد منتجات منخفضة المخزون",
    "No recent sales": "لا توجد مبيعات حديثة",
    "No sales in this period": "لا توجد مبيعات في هذه الفترة",
    "No customers in this period": "لا يوجد عملاء في هذه الفترة",
    Revenue: "الإيرادات",
    Expense: "المصروفات",
    Date: "التاريخ",
    Customer: "العميل",
    Status: "الحالة",
    Total: "الإجمالي",
    Sale: "بيع",
    Purchase: "شراء",
    Quotation: "عرض سعر",
    Expenses: "المصروفات",
    Invoices: "الفواتير",
    "Due Date": "تاريخ الاستحقاق",
    status: "الحالة",
    Amount: "المبلغ",
    "No invoices.": "لا توجد فواتير.",
    "Category Statistics": "إحصائيات الفئات",
    "Total Number Of Categories": "إجمالي عدد الفئات",
    "Total Number Of Products": "إجمالي عدد المنتجات",
    "Loading…": "جاري التحميل…",
    "Demo Mode": "وضع العرض التجريبي",
    "No store is linked to this user. Showing sample data only.":
      "لا يوجد متجر مرتبط بهذا المستخدم. يتم عرض بيانات تجريبية فقط.",
    "Not Accessible": "غير متاح",
    "Permission denied": "تم رفض الصلاحية",
    "Failed to load": "فشل التحميل",
    Unknown: "غير معروف",
    "2026 © Hamilton POS. All Right Reserved":
      "2026 © Hamilton POS. جميع الحقوق محفوظة",
    "Designed & Developed By": "تصميم وتطوير",
    Hamilton: "هاملتون",
  },
};

const inventoryDashboardStyles = `
.inventory-dashboard-pro {
  --dash-bg: #f4f7fb;
  --dash-card: rgba(255, 255, 255, 0.88);
  --dash-border: rgba(15, 23, 42, 0.08);
  --dash-text: #172033;
  --dash-muted: #5f6f86;
  --dash-accent: #ff8a1d;
  --dash-accent-blue: #1d4ed8;
  --dash-soft-shadow: 0 14px 30px rgba(16, 24, 40, 0.08);
  --dash-strong-shadow: 0 20px 44px rgba(16, 24, 40, 0.14);
  font-family: "CircularStd", "Poppins", "Nunito Sans", sans-serif;
  background:
    radial-gradient(
      circle at 8% -4%,
      rgba(255, 158, 67, 0.3),
      transparent 30%
    ),
    radial-gradient(
      circle at 94% -2%,
      rgba(37, 99, 235, 0.2),
      transparent 28%
    ),
    linear-gradient(180deg, #fbfdff 0%, #f4f7fb 48%, #eef4ff 100%);
  min-height: 100vh;
  color: var(--dash-text);
}

.inventory-dashboard-pro .content {
  padding: 28px;
}

.inventory-dashboard-pro .dashboard-section {
  margin-top: 0.35rem;
}

.inventory-dashboard-pro .row {
  --bs-gutter-y: 1.3rem;
}

.inventory-dashboard-pro .inventory-hero {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 159, 67, 0.22);
  border-radius: 24px;
  padding: 26px;
  margin-bottom: 22px;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.95),
    rgba(255, 247, 237, 0.9)
  );
  box-shadow: var(--dash-soft-shadow);
  animation: dashboardFadeUp 0.35s ease both;
}

.inventory-dashboard-pro .inventory-hero::before {
  content: "";
  position: absolute;
  inset-inline-start: -100px;
  bottom: -120px;
  width: 280px;
  height: 280px;
  border-radius: 999px;
  background: radial-gradient(
    circle,
    rgba(29, 78, 216, 0.16) 0%,
    rgba(29, 78, 216, 0) 72%
  );
}

.inventory-dashboard-pro .inventory-hero::after {
  content: "";
  position: absolute;
  width: 230px;
  height: 230px;
  inset-inline-end: -80px;
  top: -95px;
  border-radius: 999px;
  background: linear-gradient(
    135deg,
    rgba(255, 159, 67, 0.35),
    rgba(37, 99, 235, 0.14)
  );
}

.inventory-dashboard-pro .inventory-hero > * {
  position: relative;
  z-index: 1;
}

.inventory-dashboard-pro .inventory-kicker {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #9a4f0f;
  background: rgba(255, 159, 67, 0.14);
  border: 1px solid rgba(255, 159, 67, 0.18);
}

.inventory-dashboard-pro .inventory-hero h1 {
  font-size: clamp(28px, 3vw, 40px);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0;
}

.inventory-dashboard-pro .inventory-hero p {
  color: var(--dash-muted);
  margin-top: 0.3rem;
}

.inventory-dashboard-pro .inventory-hero-tools {
  gap: 0.6rem !important;
}

.inventory-dashboard-pro .live-pill {
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.01em;
  box-shadow: 0 8px 16px rgba(34, 197, 94, 0.12);
}

.inventory-dashboard-pro .inventory-hero-tools .input-icon-start {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
  padding-inline-end: 4px;
}

.inventory-dashboard-pro .inventory-hero-tools .input-icon-addon {
  inset-inline-start: 12px;
}

.inventory-dashboard-pro .inventory-hero-tools .form-control,
.inventory-dashboard-pro .inventory-hero-tools input {
  border: 0 !important;
  border-radius: 999px !important;
  min-height: 38px;
  font-size: 13px;
  background: transparent !important;
  padding-inline-start: 35px !important;
  padding-inline-end: 12px !important;
}

.inventory-dashboard-pro .card {
  border: 1px solid var(--dash-border) !important;
  border-radius: 20px !important;
  background: var(--dash-card);
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.06);
  backdrop-filter: blur(2px);
  overflow: hidden;
}

.inventory-dashboard-pro .dashboard-section .card.flex-fill:not(.sale-widget) {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  animation: dashboardFadeUp 0.4s ease both;
}

.inventory-dashboard-pro
  .dashboard-section
  .card.flex-fill:not(.sale-widget):hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 34px rgba(16, 24, 40, 0.09);
  border-color: rgba(255, 159, 67, 0.3) !important;
}

.inventory-dashboard-pro .card-header {
  border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
  background: rgba(255, 255, 255, 0.78) !important;
  padding: 18px 20px;
}

.inventory-dashboard-pro .card-title {
  font-weight: 800;
  letter-spacing: -0.01em;
}

.inventory-dashboard-pro .card-body {
  padding: 20px;
}

.inventory-dashboard-pro .card-footer {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.8),
    rgba(248, 250, 252, 0.86)
  );
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.inventory-dashboard-pro .sale-widget {
  min-height: 156px;
  border: 0 !important;
  color: #1f2b44;
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
  animation: dashboardFadeUp 0.45s ease both;
}

.inventory-dashboard-pro .stats-row > div:nth-child(1) .sale-widget {
  animation-delay: 0.02s;
}

.inventory-dashboard-pro .stats-row > div:nth-child(2) .sale-widget {
  animation-delay: 0.05s;
}

.inventory-dashboard-pro .stats-row > div:nth-child(3) .sale-widget {
  animation-delay: 0.08s;
}

.inventory-dashboard-pro .stats-row > div:nth-child(4) .sale-widget {
  animation-delay: 0.11s;
}

.inventory-dashboard-pro .sale-widget::after {
  content: "";
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  top: -90px;
  inset-inline-end: -70px;
  background: rgba(15, 23, 42, 0.05);
}

.inventory-dashboard-pro .sale-widget:hover {
  transform: translateY(-4px);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.14);
}

.inventory-dashboard-pro .sale-widget.bg-primary {
  background: linear-gradient(135deg, #fff7ee, #ffe8d3) !important;
}

.inventory-dashboard-pro .sale-widget.bg-secondary {
  background: linear-gradient(135deg, #f4f8ff, #e3ecff) !important;
}

.inventory-dashboard-pro .sale-widget.bg-teal {
  background: linear-gradient(135deg, #effcf7, #daf4ea) !important;
}

.inventory-dashboard-pro .sale-widget.bg-info {
  background: linear-gradient(135deg, #f2f6ff, #dce7ff) !important;
}

.inventory-dashboard-pro .sale-widget .card-body {
  padding: 26px 24px;
  position: relative;
  z-index: 1;
}

.inventory-dashboard-pro .sale-icon {
  width: 54px;
  height: 54px;
  min-width: 54px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.inventory-dashboard-pro .sale-widget p {
  font-size: 16px !important;
  line-height: 1.25;
  font-weight: 700;
  color: #475467 !important;
  opacity: 1;
}

.inventory-dashboard-pro .sale-widget h4 {
  font-size: 29px;
  font-weight: 800;
  color: #1e293b !important;
  margin: 0;
  letter-spacing: -0.02em;
}

.inventory-dashboard-pro .sale-widget .text-white {
  color: #1e293b !important;
}

.inventory-dashboard-pro .sale-widget .text-white-50 {
  color: #667085 !important;
}

.inventory-dashboard-pro .title-icon {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.04);
}

.inventory-dashboard-pro .info-item {
  border-radius: 18px;
  background: linear-gradient(180deg, #fff, #f8fafc) !important;
  border-color: rgba(15, 23, 42, 0.1) !important;
  min-height: 128px;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
}

.inventory-dashboard-pro .info-item:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 159, 67, 0.4) !important;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.08);
}

.inventory-dashboard-pro .info-item p {
  color: var(--dash-muted);
  font-size: 13px !important;
  font-weight: 700;
}

.inventory-dashboard-pro .info-item h5 {
  font-size: 25px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.inventory-dashboard-pro .dropdown-toggle.btn,
.inventory-dashboard-pro .btn-white {
  border: 1px solid rgba(15, 23, 42, 0.1) !important;
  border-radius: 999px !important;
  background: #fff !important;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.05);
  font-weight: 700;
}

.inventory-dashboard-pro .dropdown-menu {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  box-shadow: 0 14px 28px rgba(16, 24, 40, 0.12);
}

.inventory-dashboard-pro .dropdown-item {
  border-radius: 10px;
  font-weight: 600;
}

.inventory-dashboard-pro .dropdown-item:hover {
  background: rgba(255, 159, 67, 0.12);
  color: #6f3c09;
}

.inventory-dashboard-pro .avatar,
.inventory-dashboard-pro .avatar img {
  border-radius: 14px !important;
  object-fit: cover;
}

.inventory-dashboard-pro .sell-product > div {
  border-radius: 12px;
  padding: 8px 4px;
  transition: background-color 0.2s ease;
}

.inventory-dashboard-pro .sell-product > div:hover {
  background: rgba(255, 159, 67, 0.08);
}

.inventory-dashboard-pro .custom-table th {
  color: #667085;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.inventory-dashboard-pro .custom-table td {
  vertical-align: middle;
  padding-top: 14px;
  padding-bottom: 14px;
}

.inventory-dashboard-pro .custom-table tbody tr {
  transition: background-color 0.2s ease;
}

.inventory-dashboard-pro .custom-table tbody tr:hover {
  background: rgba(29, 78, 216, 0.05);
}

.inventory-dashboard-pro .transaction-tab {
  background: rgba(148, 163, 184, 0.08);
  border-radius: 14px;
  margin: 14px;
  padding: 4px !important;
}

.inventory-dashboard-pro .transaction-tab .nav-link {
  border: 0;
  border-radius: 10px;
  font-weight: 700;
  color: #667085;
  padding: 12px 10px;
}

.inventory-dashboard-pro .transaction-tab .nav-link.active {
  color: #ff7a00;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
}

.inventory-dashboard-pro .category-item {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 10px 12px;
  background: #fff;
}

.inventory-dashboard-pro .category-item h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.inventory-dashboard-pro .badge {
  border-radius: 999px;
  padding: 6px 10px;
  font-weight: 700;
}

.inventory-dashboard-pro .copyright-footer {
  padding: 16px 26px;
  border-top-color: rgba(15, 23, 42, 0.08) !important;
}

.inventory-dashboard-pro .copyright-footer p {
  margin-bottom: 0;
}

.inventory-dashboard-pro.is-rtl {
  direction: rtl;
  text-align: right;
}

.inventory-dashboard-pro.is-rtl .ms-2 {
  margin-left: 0 !important;
  margin-right: 0.5rem !important;
}

.inventory-dashboard-pro.is-rtl .me-1 {
  margin-right: 0 !important;
  margin-left: 0.25rem !important;
}

.inventory-dashboard-pro.is-rtl .me-2 {
  margin-right: 0 !important;
  margin-left: 0.5rem !important;
}

.inventory-dashboard-pro.is-rtl .me-3 {
  margin-right: 0 !important;
  margin-left: 1rem !important;
}

.inventory-dashboard-pro.is-rtl .text-end {
  text-align: left !important;
}

@media (max-width: 991px) {
  .inventory-dashboard-pro .inventory-hero {
    padding: 20px;
  }

  .inventory-dashboard-pro .dashboard-section .card.flex-fill:not(.sale-widget):hover {
    transform: none;
    box-shadow: 0 10px 30px rgba(16, 24, 40, 0.06);
  }
}

@media (max-width: 767px) {
  .inventory-dashboard-pro .content {
    padding: 16px;
  }

  .inventory-dashboard-pro .inventory-hero {
    border-radius: 18px;
    padding: 18px;
  }

  .inventory-dashboard-pro .sale-widget {
    min-height: 134px;
  }

  .inventory-dashboard-pro .sale-widget .card-body {
    padding: 20px 18px;
  }

  .inventory-dashboard-pro .transaction-tab {
    margin: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .inventory-dashboard-pro * {
    animation: none !important;
    transition: none !important;
  }
}

@keyframes dashboardFadeUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

const NewDashboard = ({ storeId, warehouseId }) => {
  const route = all_routes;
  const BASE_IMG = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/$/, "");
  const DEFAULT_IMAGE = "/assets/img/by-default.png";

  function resolveImageUrl(path, fallback = DEFAULT_IMAGE) {
    if (!path) return fallback || "";
    if (/^(?:https?:)?\/\//i.test(path) || /^data:/i.test(path)) return path;
    const cleaned = String(path).replace(/^\/+/, "");
    if (!BASE_IMG) return cleaned || fallback || "";
    return `${BASE_IMG}/${cleaned}`;
  }

  function handleImageError(e) {
    e.currentTarget.onerror = null;
    e.currentTarget.src = DEFAULT_IMAGE;
  }

  const [demo, setDemo] = useState(isDemoActive());

  const [totalProducts, setTotalProducts] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [prodErr, setProdErr] = useState("");

  const [totalStock, setTotalStock] = useState(0);
  const [loadingStock, setLoadingStock] = useState(true);
  const [stockErr, setStockErr] = useState("");

  const [totalOOS, setTotalOOS] = useState(0);
  const [loadingOOS, setLoadingOOS] = useState(true);
  const [oosErr, setOosErr] = useState("");

  const [invTotals, setInvTotals] = useState({
    count: 0,
    totalAmount: 0,
    totalPaid: 0,
    totalDue: 0,
  });
  const [loadingInv, setLoadingInv] = useState(true);
  const [invErr, setInvErr] = useState("");
  const [invoiceTotalsErr, setInvoiceTotalsErr] = useState("");

  const [supplierCount, setSupplierCount] = useState(0);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [supErr, setSupErr] = useState("");

  const [customerCount, setCustomerCount] = useState(0);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [custErr, setCustErr] = useState("");

  const [orderCount, setOrderCount] = useState(0);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [orderErr, setOrderErr] = useState("");

  const [custPeriod, setCustPeriod] = useState("today");
  const [loadingCO, setLoadingCO] = useState(true);
  const [coErr, setCoErr] = useState("");
  const [firstTime, setFirstTime] = useState(0);
  const [returning, setReturning] = useState(0);
  const [firstTimePct, setFirstTimePct] = useState(0);
  const [returningPct, setReturningPct] = useState(0);
  const [series, setSeries] = useState([0]);

  const [topPeriod, setTopPeriod] = useState("today");
  const [topItems, setTopItems] = useState([]);
  const [loadingTop, setLoadingTop] = useState(true);
  const [topErr, setTopErr] = useState("");

  const [lowItems, setLowItems] = useState([]);
  const [lowLoading, setLowLoading] = useState(true);
  const [lowErr, setLowErr] = useState("");

  const [rsPeriod, setRsPeriod] = useState("weekly");
  const [rsItems, setRsItems] = useState([]);
  const [rsLoading, setRsLoading] = useState(true);
  const [rsErr, setRsErr] = useState("");

  const thisYear = new Date().getFullYear();
  const [selYear, setSelYear] = useState(thisYear);
  const [stats, setStats] = useState({
    categories: Array(12).fill(""),
    series: { revenue: Array(12).fill(0) },
    totals: { revenue: 0, orders: 0, expense: null },
    yoy: { revenuePct: 0 },
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsErr, setStatsErr] = useState("");

  const yearsList = useMemo(
    () => [thisYear, thisYear - 1, thisYear - 2],
    [thisYear],
  );

  const [saleRows, setSaleRows] = useState([]);
  const [saleLoading, setSaleLoading] = useState(true);
  const [saleErr, setSaleErr] = useState("");

  const [invRows, setInvRows] = useState([]);
  const [invLoading, setInvLoading] = useState(true);

  const [tcItems, setTcItems] = useState([]);
  const [tcLoading, setTcLoading] = useState(true);
  const [tcErr, setTcErr] = useState("");
  const [topCustPeriod, setTopCustPeriod] = useState("weekly");

  const [topCat, setTopCat] = useState({
    items: [],
    totalCategories: 0,
    totalProducts: 0,
    chart: { labels: [], series: [] },
  });
  const [loadingTopCat, setLoadingTopCat] = useState(true);
  const [topCatErr, setTopCatErr] = useState("");
  const [catPeriod, setCatPeriod] = useState("weekly");

  const [period, setPeriod] = useState("weekly");
  const [heatSeries, setHeatSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const label = useMemo(
    () =>
      period === window.translate("today")
        ? window.translate("Today")
        : period === window.translate("monthly")
          ? window.translate("Monthly")
          : window.translate("Weekly"),
    [period],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingStats(true);
    setStatsErr("");
    getSalesStats({
      year: selYear,
      source: ["invoice", "online"],
      signal: ctrl.signal,
    })
      .then((data) => setStats(data))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setStats(mockDashboard.stats);
          return;
        }
        setStatsErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingStats(false));
    return () => ctrl.abort();
  }, [selYear]);

  function formatCurrency(amount, currency = "KWD", locale = "en-KW") {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  }

  const resolvedStoreId = useMemo(() => {
    if (storeId) return storeId;
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("storeId") ||
        localStorage.getItem("store_id") ||
        ""
      );
    }
    return "";
  }, [storeId]);

  const selectTopPeriod = (p) => {
    if (p !== topPeriod) {
      setLoadingTop(true);
      setTopPeriod(p);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingProducts(true);
    setProdErr("");
    getTotalProducts({
      storeId: resolvedStoreId || undefined,
      warehouseId,
      signal: ctrl.signal,
    })
      .then(({ totalProducts }) => setTotalProducts(totalProducts ?? 0))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setTotalProducts(mockDashboard.totals.totalProducts);
          return;
        }
        setProdErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingProducts(false));
    return () => ctrl.abort();
  }, [resolvedStoreId, warehouseId]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingStock(true);
    setStockErr("");
    getTotalStock({
      storeId: resolvedStoreId || undefined,
      warehouseId,
      signal: ctrl.signal,
    })
      .then(({ totalStock }) => setTotalStock(totalStock ?? 0))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setTotalStock(mockDashboard.totals.totalStock);
          return;
        }
        setStockErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingStock(false));
    return () => ctrl.abort();
  }, [resolvedStoreId, warehouseId]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingOOS(true);
    setOosErr("");
    getTotalOutOfStock({
      storeId: resolvedStoreId || undefined,
      warehouseId,
      distinct: true,
      signal: ctrl.signal,
    })
      .then(({ totalOutOfStock }) => setTotalOOS(totalOutOfStock ?? 0))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setTotalOOS(mockDashboard.totals.totalOutOfStock);
          return;
        }
        setOosErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingOOS(false));
    return () => ctrl.abort();
  }, [resolvedStoreId, warehouseId]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingInv(true);
    getSalesInvoiceTotals({ signal: ctrl.signal })
      .then(({ totals }) => setInvTotals(totals))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setInvTotals({
            count: mockDashboard.general.orders,
            totalAmount: mockDashboard.totals.totalAmount,
            totalPaid: mockDashboard.totals.totalAmount,
            totalDue: 0,
          });
          return;
        }
        setInvoiceTotalsErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingInv(false));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingSuppliers(true);
    setSupErr("");
    getTotalSuppliers({ signal: ctrl.signal })
      .then(({ totalSuppliers }) => setSupplierCount(totalSuppliers ?? 0))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setSupplierCount(mockDashboard.general.suppliers);
          return;
        }
        setSupErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingSuppliers(false));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingCustomers(true);
    setCustErr("");
    getTotalCustomers({ signal: ctrl.signal })
      .then(({ totalCustomers }) => setCustomerCount(totalCustomers ?? 0))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setCustomerCount(mockDashboard.general.customers);
          return;
        }
        setCustErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingCustomers(false));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingOrders(true);
    setOrderErr("");
    getTotalOrders({ signal: ctrl.signal })
      .then(({ totalOrders }) => setOrderCount(totalOrders ?? 0))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setOrderCount(mockDashboard.general.orders);
          return;
        }
        setOrderErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingOrders(false));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingCO(true);
    setCoErr("");
    getCustomersOverview({ period: custPeriod, signal: ctrl.signal })
      .then(({ totals, percentages, series }) => {
        setFirstTime(totals?.firstTime ?? 0);
        setReturning(totals?.returning ?? 0);
        setFirstTimePct(percentages?.firstTimePct ?? 0);
        setReturningPct(percentages?.returningPct ?? 0);
        setSeries(
          Array.isArray(series) ? series : [percentages?.firstTimePct ?? 0],
        );
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setFirstTime(18);
          setReturning(10);
          setFirstTimePct(64);
          setReturningPct(36);
          setSeries([64]);
          return;
        }
        setCoErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingCO(false));
    return () => ctrl.abort();
  }, [custPeriod]);

  function startOfTodayLocal() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  const { from, to } = useMemo(() => {
    const now = new Date();
    let start;
    if (topPeriod === "weekly") {
      start = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (topPeriod === "monthly") {
      start = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
    } else {
      start = startOfTodayLocal();
    }
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { from: fmt(start), to: fmt(now) };
  }, [topPeriod]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingTop(true);
    setTopErr("");
    getTopSellingProducts({
      storeId: resolvedStoreId || undefined,
      from,
      to,
      limit: 5,
      sortBy: "quantity",
      signal: ctrl.signal,
    })
      .then(({ items }) => setTopItems(Array.isArray(items) ? items : []))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setTopItems(mockDashboard.topProducts);
          return;
        }
        setTopErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingTop(false));
    return () => ctrl.abort();
  }, [resolvedStoreId, from, to]);

  const resolvedWarehouseId = useMemo(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("warehouseId") || "";
    }
    return "";
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLowLoading(true);
    setLowErr("");
    const lang =
      typeof navigator !== "undefined" &&
      navigator.language?.toLowerCase().startsWith("ar")
        ? "ar"
        : "en";
    getLowStock({
      storeId: resolvedStoreId || undefined,
      warehouseId: resolvedWarehouseId || undefined,
      limit: 5,
      sort: "alertGapAsc",
      lang,
      signal: ctrl.signal,
    })
      .then(({ items }) => setLowItems(Array.isArray(items) ? items : []))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setLowItems(mockDashboard.lowStock);
          return;
        }
        setLowErr(e.message || "Failed to load");
      })
      .finally(() => setLowLoading(false));
    return () => ctrl.abort();
  }, [resolvedStoreId, resolvedWarehouseId]);

  const periodLabel1 =
    topPeriod === `${window.translate("today")}`
      ? `${window.translate("Today")}`
      : topPeriod === `${window.translate("weekly")}`
        ? `${window.translate("Weekly")}`
        : `${window.translate("Monthly")}`;

  const totalRevenue = useMemo(
    () => topItems.reduce((sum, it) => sum + Number(it.revenue || 0), 0),
    [topItems],
  );

  const customerChart = useMemo(
    () => ({
      chart: { sparkline: { enabled: true } },
      plotOptions: {
        radialBar: {
          hollow: { size: "60%" },
          dataLabels: {
            value: {
              formatter: (val) => `${Math.round(val)}%`,
            },
          },
        },
      },
      labels: [`${window.translate("First Time")}`],
    }),
    [],
  );

  const periodLabel =
    custPeriod === "today"
      ? window.translate("Today")
      : custPeriod === "weekly"
        ? window.translate("Weekly")
        : window.translate("Monthly");

  const isArabicUI = useMemo(() => {
    const stored = (
      localStorage.getItem("lang") ||
      localStorage.getItem("i18nextLng") ||
      ""
    ).toLowerCase();
    if (stored) return stored.startsWith("ar");
    return (
      typeof navigator !== "undefined" &&
      navigator.language?.toLowerCase().startsWith("ar")
    );
  }, []);

  const t = (key) => {
    const translated =
      typeof window !== "undefined" && window?.translate
        ? window.translate(key)
        : key;
    if (isArabicUI && translated === key)
      return DASHBOARD_TRANSLATIONS.ar[key] || key;
    return translated;
  };

  const tr = (key, arFallback) => {
    const out = t(key);
    return isArabicUI && out === key ? arFallback : out;
  };

  const isPermissionDeniedError = (msg = "") => {
    const m = String(msg).toLowerCase();
    return (
      /403|forbidden|permission|access\s*denied|not\s*allowed|unauthorized/.test(
        m,
      ) || /صلاحية|غير\s*مسموح|ممنوع/.test(m)
    );
  };

  const renderAccessError = (msg, opts = {}) => {
    if (!msg) return null;
    const light = Boolean(opts.light);
    const className = opts.className || "mt-1";
    const baseTextClass = light ? "text-white-50" : "text-muted";
    if (isPermissionDeniedError(msg)) {
      return (
        <div className={className}>
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle">
            {tr("Not Accessible", "غير متاح")}
          </span>
          <div className={`${baseTextClass} small mt-1`}>
            {tr("Permission denied", "تم رفض الصلاحية")}
          </div>
        </div>
      );
    }
    return (
      <div
        className={`${light ? "text-white-50" : "text-danger"} small ${className}`}
      >
        {msg}
      </div>
    );
  };

  const periodLabelRS =
    rsPeriod === "today"
      ? window.translate("Today")
      : rsPeriod === "monthly"
        ? window.translate("Monthly")
        : window.translate("Weekly");

  const lang =
    typeof navigator !== "undefined" &&
    navigator.language?.toLowerCase().startsWith("ar")
      ? "ar"
      : "en";

  const selectRsPeriod = (p) => {
    if (p !== rsPeriod) {
      setRsLoading(true);
      setRsPeriod(p);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    setRsErr("");
    getRecentSales({
      period: rsPeriod,
      limit: 5,
      source: ["invoice", "online"],
      lang,
      signal: ctrl.signal,
    })
      .then(({ items }) => setRsItems(Array.isArray(items) ? items : []))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setRsItems(mockDashboard.recentSales);
          return;
        }
        setRsErr(e.message || window.translate("Failed to load"));
      })
      .finally(() => setRsLoading(false));
    return () => ctrl.abort();
  }, [rsPeriod, lang]);

  const fallbackImg = DEFAULT_IMAGE;

  useEffect(() => {
    const ctrl1 = new AbortController();
    const ctrl2 = new AbortController();

    setSaleLoading(true);
    setSaleErr("");
    getRecentTransactions({
      type: "sale",
      period: "weekly",
      limit: 5,
      lang,
      signal: ctrl1.signal,
    })
      .then(({ items }) => setSaleRows(Array.isArray(items) ? items : []))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setSaleRows(mockDashboard.saleRows);
          return;
        }
        setSaleErr(
          e.status === 403
            ? window.translate(
                "No store is linked to this user. Link a store to view recent sales.",
              )
            : e.message || window.translate("Failed to load"),
        );
      })
      .finally(() => setSaleLoading(false));

    setInvLoading(true);
    setInvErr("");
    getRecentTransactions({
      type: "invoice",
      period: "weekly",
      limit: 5,
      lang,
      signal: ctrl2.signal,
    })
      .then(({ items }) => setInvRows(Array.isArray(items) ? items : []))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setSaleRows(mockDashboard.saleRows);
          return;
        }
        setSaleErr(
          e.status === 403
            ? "No store is linked to this user. Link a store to view recent sales."
            : e.message || "Failed to load",
        );
      })
      .finally(() => setInvLoading(false));

    return () => {
      ctrl1.abort();
      ctrl2.abort();
    };
  }, [lang]);

  function fmtDate(iso) {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  }

  function fmtCurrency(n, currency = "KWD", locale = "en-KW") {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  }

  function saleBadge(status) {
    if (status === "Completed")
      return { cls: "badge badge-success badge-xs", text: "Completed" };
    if (status === "Partial")
      return { cls: "badge badge-warning badge-xs", text: "Partial" };
    if (status === "Draft")
      return { cls: "badge badge-pink badge-xs", text: "Draft" };
    return { cls: "badge bg-secondary badge-xs", text: status || "—" };
  }

  function invBadge(status) {
    if (status === "Paid")
      return { cls: "badge badge-success badge-xs", text: "Paid" };
    if (status === "Overdue")
      return { cls: "badge badge-warning badge-xs", text: "Overdue" };
    if (status === "Unpaid")
      return { cls: "badge badge-danger badge-xs", text: "Unpaid" };
    return { cls: "badge bg-secondary badge-xs", text: status || "—" };
  }

  const avatarFallback = DEFAULT_IMAGE;

  function formatWhen(iso) {
    const d = new Date(iso);
    const now = new Date();
    const startToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    return d >= startToday
      ? "Today"
      : d.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  }

  function statusBadge(item) {
    if (item.source === "online") {
      if (item.status === "Completed")
        return { cls: "badge badge-success badge-xs", text: "Completed" };
      if (item.paymentStatus === "Overdue")
        return { cls: "badge badge-cyan badge-xs", text: "Onhold" };
      return { cls: "badge bg-purple badge-xs", text: "Processing" };
    }
    if (item.status === "Paid")
      return { cls: "badge badge-success badge-xs", text: "Completed" };
    if (item.status === "Overdue")
      return { cls: "badge badge-cyan badge-xs", text: "Onhold" };
    if (item.status === "Unpaid")
      return { cls: "badge bg-purple badge-xs", text: "Processing" };
    return { cls: "badge bg-secondary badge-xs", text: item.status || "—" };
  }

  const periodLabelTC = useMemo(() => {
    switch (topCustPeriod) {
      case "today":
        return window.translate("Today");
      case "monthly":
        return window.translate("Monthly");
      default:
        return window.translate("Weekly");
    }
  }, [topCustPeriod]);

  const formatMoney = (n) =>
    typeof n === "number"
      ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
      : "—";

  useEffect(() => {
    const ctrl = new AbortController();
    setTcLoading(true);
    setTcErr("");
    getTopCustomers({ period: topCustPeriod, limit: 5, signal: ctrl.signal })
      .then(({ items }) => setTcItems(items || []))
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setTcItems(mockDashboard.topCustomers);
          return;
        }
        setTcErr(e.message || window.translate("Failed to load"));
      })
      .finally(() => setTcLoading(false));
    return () => ctrl.abort();
  }, [topCustPeriod]);

  const periodLabelCat = useMemo(() => {
    if (catPeriod === "today") return window.translate("Today");
    if (catPeriod === "monthly") return window.translate("Monthly");
    return window.translate("Weekly");
  }, [catPeriod]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoadingTopCat(true);
    setTopCatErr("");
    getTopCategories({ period: catPeriod, limit: 3, signal: ctrl.signal })
      .then((data) =>
        setTopCat(
          data || {
            items: [],
            totalCategories: 0,
            totalProducts: 0,
            chart: { labels: [], series: [] },
          },
        ),
      )
      .catch((e) => {
        if (e.name === "AbortError") return;
        if (isDemoActive()) {
          setDemo(true);
          setTopCat(mockDashboard.topCategories);
          return;
        }
        setTopCatErr(e.message || "Failed to load");
      })
      .finally(() => setLoadingTopCat(false));
    return () => ctrl.abort();
  }, [catPeriod]);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setErr("");
    getOrderStatistics({
      period,
      metric: "count",
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      signal: ctrl.signal,
    })
      .then(({ series }) => setHeatSeries(series || []))
      .catch((e) => {
        if (e.name === "AbortError" || e.name === "CanceledError") return;
        if (isDemoActive()) {
          setDemo(true);
          setHeatSeries(mockDashboard.heatSeries);
          return;
        }
        setErr(e.message || "Failed to load order statistics");
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [period]);

  const doughnutData = useMemo(() => {
    const labels = topCat?.chart?.labels || [];
    const series = topCat?.chart?.series || [];
    return {
      labels,
      datasets: [
        {
          data: series,
          backgroundColor: [
            "#5e60ce",
            "#ff922b",
            "#22b8cf",
            "#845ef7",
            "#ffa8a8",
            "#51cf66",
          ],
          borderWidth: 0,
        },
      ],
    };
  }, [topCat]);

  const doughnutOptions = useMemo(
    () => ({
      cutout: "60%",
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      maintainAspectRatio: false,
    }),
    [],
  );

  const heatOptionsBase = useMemo(
    () => ({
      chart: { type: "heatmap", height: 370 },
      plotOptions: {
        heatmap: {
          radius: 4,
          enableShades: false,
          colorScale: {
            ranges: [
              { from: 0, to: 99, color: "#FFE3CB" },
              { from: 100, to: 200, color: "#FE9F43" },
            ],
          },
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      grid: { padding: { top: -20, bottom: 0, left: 0, right: 0 } },
      yaxis: { labels: { offsetX: -15 } },
    }),
    [],
  );

  const heatOptions = useMemo(
    () => ({
      ...heatOptionsBase,
      plotOptions: {
        heatmap: {
          ...heatOptionsBase.plotOptions.heatmap,
          colorScale: {
            ranges: [
              { from: 1, to: 1, color: "#6B7280" },
              { from: 0, to: 0, color: "#FFF4E8" },
              { from: 2, to: 99, color: "#FFE3CB" },
              { from: 100, to: 200, color: "#FE9F43" },
            ],
          },
        },
      },
    }),
    [heatOptionsBase],
  );

  const highlightedSeries = useMemo(
    () =>
      (heatSeries || []).map((row) => ({
        name: row.name,
        data: (row.data || []).map((pt) =>
          pt && typeof pt.y === "number" && pt.y === 1
            ? { ...pt, fillColor: "#6B7280" }
            : pt,
        ),
      })),
    [heatSeries],
  );

  const chartOptions = useMemo(
    () => ({
      chart: { type: "bar", toolbar: { show: false } },
      xaxis: { categories: stats.categories || [] },
      dataLabels: { enabled: false },
      plotOptions: { bar: { columnWidth: "45%", borderRadius: 6 } },
      yaxis: {
        labels: {
          formatter: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`),
        },
      },
      tooltip: { y: { formatter: (val) => formatCurrency(val) } },
    }),
    [stats.categories],
  );

  const chartSeries = useMemo(
    () => [{ name: "Revenue", data: stats.series?.revenue || [] }],
    [stats.series],
  );

  const revenueYoY = stats.yoy?.revenuePct ?? 0;
  const revBadgeUp = revenueYoY >= 0;

  function DemoBanner({ reason }) {
    return (
      <div className="rounded-3 border border-dashed p-3 mb-3 bg-warning-subtle text-body">
        <div className="fw-semibold">{window.translate("Demo Mode")}</div>
        <div className="small opacity-75">
          {window.translate(
            "No store is linked to this user. Showing sample data only.",
          )}
          {reason ? <span className="ms-1">({reason})</span> : null}
        </div>
      </div>
    );
  }

  DemoBanner.propTypes = { reason: PropTypes.string };
  DemoBanner.defaultProps = { reason: "" };

  // Computed dropdown item style (called once per render, reactive to dir changes)
  const ddItemStyle = getDropdownItemStyle();

  return (
    <div
      className={`page-wrapper inventory-dashboard-pro ${isArabicUI ? "is-rtl" : "is-ltr"}`}
    >
      <style>{inventoryDashboardStyles}</style>
      <div className="content">
        {demo && <DemoBanner reason={getDemoReason()} />}
        <div className="inventory-hero d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <span className="inventory-kicker mb-3">
              <i className="ti ti-layout-dashboard" />
              {t("Inventory Dashboard")}
            </span>
            <h1 className="mb-2">{t("Welcome Admin")}</h1>
            <p className="mb-0">
              {t(
                "Here is your inventory, stock, and sales performance overview.",
              )}
            </p>
          </div>
          <div className="inventory-hero-tools d-flex align-items-center gap-2 flex-wrap">
            <span className="badge live-pill bg-success-subtle text-success border border-success-subtle">
              <i className="ti ti-activity me-1" />
              {t("Live overview")}
            </span>
            <div className="hero-date-range input-icon-start position-relative">
              <span className="input-icon-addon fs-16 text-gray-9">
                <i className="ti ti-calendar" />
              </span>
              <PredefinedDateRanges />
            </div>
          </div>
        </div>

        {/* ── Top Stats Row ── */}
        <div className="row stats-row dashboard-section">
          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-primary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-primary">
                  <i className="ti ti-file-text fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1 fs-2">{t("Total Products")}</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {loadingProducts
                        ? "—"
                        : Number(totalProducts).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                    </h4>
                  </div>
                  {renderAccessError(prodErr, {
                    light: true,
                    className: "mt-1",
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-secondary sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-secondary">
                  <i className="ti ti-repeat fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1 fs-2">{t("Total Stock")}</p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {loadingStock
                        ? "—"
                        : Number(totalStock).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                    </h4>
                  </div>
                  {renderAccessError(stockErr, {
                    light: true,
                    className: "mt-1",
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-teal sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-teal">
                  <i className="ti ti-gift fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1 fs-2">
                    {t("Total Out of Stock")}
                  </p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {loadingOOS
                        ? "—"
                        : Number(totalOOS).toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                          })}
                    </h4>
                  </div>
                  {renderAccessError(oosErr, {
                    light: true,
                    className: "mt-1",
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-sm-6 col-12 d-flex">
            <div className="card bg-info sale-widget flex-fill">
              <div className="card-body d-flex align-items-center">
                <span className="sale-icon bg-white text-info">
                  <i className="ti ti-brand-pocket fs-24" />
                </span>
                <div className="ms-2">
                  <p className="text-white mb-1 fs-2">
                    {t("Total sales invoice")}
                  </p>
                  <div className="d-inline-flex align-items-center flex-wrap gap-2">
                    <h4 className="text-white">
                      {loadingInv
                        ? "—"
                        : `KWD ${Number(invTotals?.totalAmount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                    </h4>
                  </div>
                  {renderAccessError(invoiceTotalsErr, {
                    light: true,
                    className: "mt-1",
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Overall Information + Customer Overview ── */}
        <div className="row dashboard-section">
          <div className="col-12 d-flex">
            <div className="card flex-fill w-100">
              <div className="card-header">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-info fs-16 me-2">
                    <i className="ti ti-info-circle" />
                  </span>
                  <h5 className="card-title mb-0">
                    {t("Overall Information")}
                  </h5>
                </div>
              </div>

              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="info-item border bg-light p-3 text-center">
                      <div className="mb-3 text-info fs-24">
                        <i className="ti ti-user-check" />
                      </div>
                      <p className="mb-1">{t("Suppliers")}</p>
                      <h5>
                        {loadingSuppliers
                          ? "—"
                          : Number(supplierCount).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                      </h5>
                      {renderAccessError(supErr, { className: "mt-1" })}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item border bg-light p-3 text-center">
                      <div className="mb-3 text-orange fs-24">
                        <i className="ti ti-users" />
                      </div>
                      <p className="mb-1" style={{ fontSize: "11px" }}>
                        {t("Customers")}
                      </p>
                      <h5>
                        {loadingCustomers
                          ? "—"
                          : Number(customerCount).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                      </h5>
                      {renderAccessError(custErr, { className: "mt-1" })}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="info-item border bg-light p-3 text-center">
                      <div className="mb-3 text-teal fs-24">
                        <i className="ti ti-shopping-cart" />
                      </div>
                      <p className="mb-1">{t("Orders")}</p>
                      <h5>
                        {loadingOrders
                          ? "—"
                          : Number(orderCount).toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                      </h5>
                      {renderAccessError(orderErr, { className: "mt-1" })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-footer pb-sm-0">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <h6>{t("Customers Overview")}</h6>
                  <div className="dropdown dropdown-wraper">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-sm"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="ti ti-calendar me-1" />
                      {periodLabel}
                    </Link>
                    <ul className="dropdown-menu p-3">
                      <li>
                        <Link
                          to="#"
                          className="dropdown-item"
                          style={ddItemStyle}
                          onClick={() => setCustPeriod("today")}
                        >
                          <span>{window.translate("Today")}</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          className="dropdown-item"
                          style={ddItemStyle}
                          onClick={() => setCustPeriod("weekly")}
                        >
                          <span>{window.translate("Weekly")}</span>
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="#"
                          className="dropdown-item"
                          style={ddItemStyle}
                          onClick={() => setCustPeriod("monthly")}
                        >
                          <span>{window.translate("Monthly")}</span>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="row gy-4 justify-content-center align-items-center customer-overview">
                  <div className="col-12 col-sm-8 col-md-5 col-lg-4 d-flex justify-content-center">
                    <div className="chart-wrap mx-auto">
                      <Chart
                        options={customerChart}
                        series={series}
                        type="radialBar"
                        height={180}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-sm-10 col-md-6 col-lg-5 d-flex justify-content-center">
                    <div className="w-100" style={{ maxWidth: 420 }}>
                      <div className="row g-3 justify-content-center">
                        <div className="col-6">
                          <div
                            className="text-center text-md-start border-0 border-md-end pe-0 pe-md-3"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <h2 className="mb-1">
                              {loadingCO
                                ? "—"
                                : Number(firstTime).toLocaleString()}
                            </h2>
                            <p className="text-orange mb-2">
                              {t("First Time")}
                            </p>
                            <span className="badge badge-success badge-xs d-inline-flex align-items-center">
                              <i className="ti ti-arrow-up-left me-1" />
                              {loadingCO ? "—" : `${firstTimePct}%`}
                            </span>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            className="text-center text-md-start"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <h2 className="mb-1">
                              {loadingCO
                                ? "—"
                                : Number(returning).toLocaleString()}
                            </h2>
                            <p className="text-teal mb-2">{t("Return")}</p>
                            <span className="badge badge-success badge-xs d-inline-flex align-items-center">
                              <i className="ti ti-arrow-up-left me-1" />
                              {loadingCO ? "—" : `${returningPct}%`}
                            </span>
                          </div>
                        </div>
                      </div>
                      {renderAccessError(coErr, { className: "mt-2" })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Top Selling / Low Stock / Recent Sales ── */}
        <div className="row dashboard-section">
          {/* Top Selling Products */}
          <div className="col-xxl-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-pink fs-16 me-2">
                    <i className="ti ti-box" />
                  </span>
                  <h5 className="card-title mb-0">
                    {t("Top Selling Products")}
                  </h5>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-calendar me-1" />
                    {periodLabel1}
                    {loadingTop && (
                      <span
                        className="spinner-border spinner-border-sm ms-2"
                        role="status"
                        aria-hidden="true"
                        style={{ width: "1rem", height: "1rem" }}
                      />
                    )}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          selectTopPeriod("today");
                        }}
                      >
                        <span>{t("Today")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          selectTopPeriod("weekly");
                        }}
                      >
                        <span>{t("Weekly")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          selectTopPeriod("monthly");
                        }}
                      >
                        <span>{t("Monthly")}</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="card-body sell-product">
                {renderAccessError(topErr, { className: "mb-2" })}
                {loadingTop ? (
                  <>
                    {[...Array(2)].map((_, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between border-bottom py-2 opacity-50"
                      >
                        <div className="d-flex align-items-center">
                          <span
                            className="avatar avatar-lg bg-light rounded-circle"
                            style={{ width: 48, height: 48 }}
                          />
                          <div className="ms-2">
                            <div className="placeholder-glow">
                              <span className="placeholder col-8" />
                            </div>
                            <div className="placeholder-glow">
                              <span className="placeholder col-6" />
                            </div>
                          </div>
                        </div>
                        <span className="badge bg-outline-secondary badge-xs">
                          —
                        </span>
                      </div>
                    ))}
                  </>
                ) : topItems.length === 0 ? (
                  <div className="text-muted py-3">
                    {t("No sales in this period")}
                  </div>
                ) : (
                  topItems.map((it, idx) => {
                    const name =
                      (it.productName &&
                        (it.productName.en || it.productName.ar)) ||
                      it.sku ||
                      "Unnamed Product";
                    const img = resolveImageUrl(it.image);
                    const qty = Number(it.quantitySold || 0);
                    const revenue = Number(it.revenue || 0);
                    const share =
                      totalRevenue > 0
                        ? Math.round((revenue / totalRevenue) * 100)
                        : 0;
                    const rowClass =
                      idx < topItems.length - 1 ? "border-bottom" : "";
                    return (
                      <div
                        className={`d-flex align-items-center justify-content-between ${rowClass}`}
                        key={String(it.productId || idx)}
                      >
                        <div className="d-flex align-items-center">
                          <Link to="#" className="avatar avatar-lg">
                            <img
                              src={img}
                              alt="img"
                              onError={handleImageError}
                            />
                          </Link>
                          <div className="ms-2">
                            <h6 className="fw-bold mb-1">
                              <Link to="#">{name}</Link>
                            </h6>
                            <div className="d-flex align-items-center item-list">
                              <p className="me-3 mb-0">
                                {formatCurrency(revenue)}
                              </p>
                              <p className="mb-0">
                                {qty.toLocaleString()}{" "}
                                {window.translate("Sales")}
                              </p>
                            </div>
                          </div>
                        </div>
                        <span
                          className={`badge ${share >= 25 ? "bg-outline-success" : "bg-outline-secondary"} badge-xs d-inline-flex align-items-center`}
                        >
                          <i
                            className={`ti ${share >= 25 ? "ti-arrow-up-left" : "ti-minus"} me-1`}
                          />
                          {share}%
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Low Stock Products */}
          <div className="col-xxl-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-danger fs-16 me-2">
                    <i className="ti ti-alert-triangle" />
                  </span>
                  <h5 className="card-title mb-0">{t("Low Stock Products")}</h5>
                </div>
                <Link
                  to={route.lowstock}
                  className="fs-13 fw-bold text-decoration-underline"
                >
                  {t("View All")}
                </Link>
              </div>

              <div className="card-body" aria-busy={lowLoading}>
                {renderAccessError(lowErr, { className: "mb-2" })}
                {lowLoading ? (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between mb-4 opacity-50"
                      >
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="avatar avatar-lg bg-light rounded-circle"
                            style={{ width: 48, height: 48 }}
                          />
                          <div className="ms-2">
                            <div className="placeholder-glow">
                              <span className="placeholder col-8" />
                            </div>
                            <div className="placeholder-glow">
                              <span className="placeholder col-6" />
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="placeholder-glow">
                            <span className="placeholder col-4" />
                          </div>
                          <div className="placeholder-glow">
                            <span className="placeholder col-3" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : lowItems.length === 0 ? (
                  <div className="text-muted py-3">
                    {t("No low stock items")}
                  </div>
                ) : (
                  lowItems.map((it, idx) => {
                    const name =
                      typeof it.productName === "string"
                        ? it.productName
                        : it.productName?.en || it.productName?.ar || "Unnamed";
                    const img = resolveImageUrl(it.image);
                    const sku =
                      it.sku && it.sku !== "N/A"
                        ? it.sku
                        : String(it.productId).slice(-6);
                    const inStock = Number(it.quantity ?? 0);
                    const rowClass =
                      idx < lowItems.length - 1 ? "mb-4" : "mb-0";
                    return (
                      <div
                        key={String(it.id || it.productId || idx)}
                        className={`d-flex align-items-center justify-content-between ${rowClass}`}
                      >
                        <div className="d-flex align-items-center">
                          <Link to="#" className="avatar avatar-lg">
                            <img
                              src={img}
                              alt="product"
                              onError={handleImageError}
                            />
                          </Link>
                          <div className="ms-2">
                            <h6 className="fw-bold mb-1">
                              <Link to="#">{name}</Link>
                            </h6>
                            <p className="fs-13 mb-0">
                              {window.translate("SKU")} : #{sku}
                            </p>
                          </div>
                        </div>
                        <div className="text-end">
                          <p className="fs-13 mb-1">{t("Instock")}</p>
                          <h6 className="text-orange fw-bold">
                            {inStock.toLocaleString(undefined, {
                              maximumFractionDigits: 0,
                            })}
                          </h6>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Recent Sales */}
          <div className="col-xxl-4 col-md-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-pink fs-16 me-2">
                    <i className="ti ti-box" />
                  </span>
                  <h5 className="card-title mb-0">{t("Recent Sales")}</h5>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-calendar me-1" />
                    {periodLabelRS}
                    {rsLoading && (
                      <span
                        className="spinner-border spinner-border-sm ms-2"
                        role="status"
                        aria-hidden="true"
                        style={{ width: "1rem", height: "1rem" }}
                      />
                    )}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          selectRsPeriod("today");
                        }}
                      >
                        <span>{window.translate("Today")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          selectRsPeriod("weekly");
                        }}
                      >
                        <span>{window.translate("Weekly")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={(e) => {
                          e.preventDefault();
                          selectRsPeriod("monthly");
                        }}
                      >
                        <span>{window.translate("Monthly")}</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="card-body" aria-busy={rsLoading}>
                {renderAccessError(rsErr, { className: "mb-3" })}
                {rsLoading ? (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between mb-4 opacity-50"
                      >
                        <div className="d-flex align-items-center">
                          <span
                            className="avatar avatar-lg bg-light rounded-circle"
                            style={{ width: 48, height: 48 }}
                          />
                          <div className="ms-2">
                            <div className="placeholder-glow">
                              <span className="placeholder col-8" />
                            </div>
                            <div className="placeholder-glow">
                              <span className="placeholder col-6" />
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="placeholder-glow">
                            <span className="placeholder col-4" />
                          </div>
                          <div className="placeholder-glow mt-1">
                            <span className="placeholder col-5" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : rsItems.length === 0 ? (
                  <div className="text-muted">{t("No recent sales")}</div>
                ) : (
                  rsItems.map((it, idx) => {
                    const { cls, text } = statusBadge(it);
                    const img = resolveImageUrl(it.image) || fallbackImg;
                    const title =
                      it.title || (it.reference ? `#${it.reference}` : "—");
                    const cat =
                      it.category ||
                      (it.source === "invoice" ? "Invoice" : "—");
                    const amt = formatCurrency(it.amount);
                    const when = formatWhen(it.date);
                    const rowClass = idx < rsItems.length - 1 ? "mb-4" : "mb-0";
                    return (
                      <div
                        key={it.id}
                        className={`d-flex align-items-center justify-content-between ${rowClass}`}
                      >
                        <div className="d-flex align-items-center">
                          <Link to="#" className="avatar avatar-lg">
                            <img
                              src={img}
                              alt="img"
                              onError={handleImageError}
                            />
                          </Link>
                          <div className="ms-2">
                            <h6 className="fw-bold mb-1">
                              <Link to="#">{title}</Link>
                            </h6>
                            <div className="d-flex align-items-center item-list">
                              <p className="mb-0 me-3">{cat}</p>
                              <p className="text-gray-9 mb-0">{amt}</p>
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <p className="fs-13 mb-1">{when}</p>
                          <span
                            className={`${cls} d-inline-flex align-items-center`}
                          >
                            <i className="ti ti-circle-filled fs-5 me-1" />
                            {text}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Sales Statics + Recent Transactions ── */}
        <div className="row dashboard-section">
          {/* Sales Statics */}
          <div className="col-xl-6 col-sm-12 col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-danger fs-16 me-2">
                    <i className="ti ti-alert-triangle" />
                  </span>
                  <h5 className="card-title mb-0">{t("Sales Statistics")}</h5>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-calendar me-1" />
                    {selYear}
                    {loadingStats && (
                      <span
                        className="spinner-border spinner-border-sm ms-2"
                        role="status"
                        aria-hidden="true"
                        style={{ width: "1rem", height: "1rem" }}
                      />
                    )}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {yearsList.map((y) => (
                      <li key={y}>
                        <Link
                          to="#"
                          className="dropdown-item"
                          style={ddItemStyle}
                          onClick={(e) => {
                            e.preventDefault();
                            setSelYear(y);
                          }}
                        >
                          <span>{y}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="card-body pb-0" aria-busy={loadingStats}>
                {renderAccessError(statsErr, { className: "mb-2" })}
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <div className="border p-2 br-8">
                    <h5 className="d-inline-flex align-items-center text-teal gap-1">
                      {loadingStats
                        ? "—"
                        : formatCurrency(stats.totals?.revenue || 0)}
                      <span
                        className={`badge ${revBadgeUp ? "badge-success" : "badge-danger"} badge-xs d-inline-flex align-items-center ms-2`}
                      >
                        <i
                          className={`ti ${revBadgeUp ? "ti-arrow-up-left" : "ti-arrow-down-right"} me-1`}
                        />
                        {loadingStats ? "—" : `${Math.abs(revenueYoY)}%`}
                      </span>
                    </h5>
                    <p>{t("Revenue")}</p>
                  </div>
                  <div className="border p-2 br-8">
                    <h5 className="d-inline-flex align-items-center text-orange gap-1">
                      {loadingStats
                        ? "—"
                        : stats.totals?.expense == null
                          ? formatCurrency(0)
                          : formatCurrency(stats.totals.expense)}
                      <span
                        className="badge badge-danger badge-xs d-inline-flex align-items-center ms-2"
                        style={{ display: "none" }}
                      >
                        <i className="ti ti-arrow-down-right me-1" /> 0%
                      </span>
                    </h5>
                    <p>{t("Expense")}</p>
                  </div>
                </div>
                <div id="sales-statistics" className="mt-2">
                  {loadingStats ? (
                    <div className="placeholder-glow" style={{ height: 290 }}>
                      <span
                        className="placeholder col-12"
                        style={{ height: 290, display: "block" }}
                      />
                    </div>
                  ) : (
                    <ReactApexChart
                      options={chartOptions}
                      series={chartSeries}
                      type="bar"
                      height={290}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="col-xl-6 col-sm-12 col-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-orange fs-16 me-2">
                    <i className="ti ti-flag" />
                  </span>
                  <h5 className="card-title mb-0">
                    {t("Recent Transactions")}
                  </h5>
                </div>
                <Link
                  to={route.onlineorder}
                  className="fs-13 fw-medium text-decoration-underline"
                >
                  {t("View All")}
                </Link>
              </div>

              <div className="card-body p-0">
                <ul className="nav nav-tabs nav-justified transaction-tab">
                  <li className="nav-item">
                    <Link
                      className="nav-link active"
                      to="#sale"
                      data-bs-toggle="tab"
                    >
                      {t("Sale")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="#purchase-transaction"
                      data-bs-toggle="tab"
                    >
                      {t("Purchase")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="#quotation"
                      data-bs-toggle="tab"
                    >
                      {t("Quotation")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="#expenses"
                      data-bs-toggle="tab"
                    >
                      {t("Expenses")}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      className="nav-link"
                      to="#invoices"
                      data-bs-toggle="tab"
                    >
                      {t("Invoices")}
                    </Link>
                  </li>
                </ul>

                <div className="tab-content">
                  {/* SALE TAB */}
                  <div className="tab-pane show active" id="sale">
                    <div className="table-responsive">
                      <table className="table table-borderless custom-table">
                        <thead className="thead-light">
                          <tr>
                            <th>{t("Date")}</th>
                            <th>{t("Customer")}</th>
                            <th>{t("Status")}</th>
                            <th>{t("Total")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {saleErr ? (
                            <tr>
                              <td colSpan={4}>
                                {renderAccessError(saleErr, {
                                  className: "mb-0",
                                })}
                              </td>
                            </tr>
                          ) : saleLoading ? (
                            [...Array(3)].map((_, i) => (
                              <tr key={i} className="opacity-50">
                                <td>
                                  <span className="placeholder col-6" />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center file-name-icon">
                                    <span className="avatar avatar-md bg-light" />
                                    <div className="ms-2">
                                      <h6 className="fw-medium mb-1">
                                        <span className="placeholder col-8" />
                                      </h6>
                                      <span className="placeholder col-5" />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="placeholder col-6" />
                                </td>
                                <td className="fs-16 fw-bold text-gray-9">
                                  <span className="placeholder col-6" />
                                </td>
                              </tr>
                            ))
                          ) : saleRows.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-muted">
                                {t("No recent sales")}
                              </td>
                            </tr>
                          ) : (
                            saleRows.map((r) => {
                              const { cls, text } = saleBadge(r.status);
                              const name =
                                r.customer?.name || r.customer?.id || "—";
                              return (
                                <tr key={r.id}>
                                  <td>{fmtDate(r.date)}</td>
                                  <td>
                                    <div className="d-flex align-items-center file-name-icon">
                                      <span className="avatar avatar-md">
                                        <img
                                          src={avatarFallback}
                                          className="img-fluid"
                                          alt="customer"
                                          onError={handleImageError}
                                        />
                                      </span>
                                      <div className="ms-2">
                                        <h6 className="fw-medium mb-0">
                                          <Link to="#">{name}</Link>
                                        </h6>
                                        <span className="fs-13 text-orange">
                                          #{r.orderId}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className={cls}>
                                      <i className="ti ti-circle-filled fs-5 me-1" />
                                      {text}
                                    </span>
                                  </td>
                                  <td className="fs-16 fw-bold text-gray-9">
                                    {fmtCurrency(r.total)}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div
                    className="tab-pane fade"
                    id="purchase-transaction"
                  ></div>
                  <div className="tab-pane" id="quotation"></div>
                  <div className="tab-pane fade" id="expenses"></div>

                  {/* INVOICES TAB */}
                  <div className="tab-pane" id="invoices">
                    <div className="table-responsive">
                      <table className="table table-borderless custom-table">
                        <thead className="thead-light">
                          <tr>
                            <th>{window.translate("Customer")}</th>
                            <th>{window.translate("Due Date")}</th>
                            <th>{window.translate("status")}</th>
                            <th>{window.translate("Amount")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invErr ? (
                            <tr>
                              <td colSpan={4}>
                                {renderAccessError(invErr, {
                                  className: "mb-0",
                                })}
                              </td>
                            </tr>
                          ) : invLoading ? (
                            [...Array(3)].map((_, i) => (
                              <tr key={i} className="opacity-50">
                                <td>
                                  <div className="d-flex align-items-center file-name-icon">
                                    <span className="avatar avatar-md bg-light" />
                                    <div className="ms-2">
                                      <h6 className="fw-medium mb-1">
                                        <span className="placeholder col-8" />
                                      </h6>
                                      <span className="placeholder col-4" />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span className="placeholder col-6" />
                                </td>
                                <td>
                                  <span className="placeholder col-5" />
                                </td>
                                <td className="text-gray-9">
                                  <span className="placeholder col-6" />
                                </td>
                              </tr>
                            ))
                          ) : invRows.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="text-muted">
                                {window.translate("No invoices.")}
                              </td>
                            </tr>
                          ) : (
                            invRows.map((r) => {
                              const { cls, text } = invBadge(r.status);
                              const name = r.customer?.name || "—";
                              return (
                                <tr key={r.id}>
                                  <td>
                                    <div className="d-flex align-items-center file-name-icon">
                                      <span className="avatar avatar-md">
                                        <img
                                          src={avatarFallback}
                                          className="img-fluid"
                                          alt="customer"
                                          onError={handleImageError}
                                        />
                                      </span>
                                      <div className="ms-2">
                                        <h6 className="fw-medium mb-0">
                                          <Link to="#">{name}</Link>
                                        </h6>
                                        <span className="fs-13 text-orange">
                                          #{r.reference}
                                        </span>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    {r.dueDate ? fmtDate(r.dueDate) : "—"}
                                  </td>
                                  <td>
                                    <span className={cls}>
                                      <i className="ti ti-circle-filled fs-5 me-1" />
                                      {text}
                                    </span>
                                  </td>
                                  <td className="text-gray-9">
                                    {fmtCurrency(r.amount)}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Top Customers / Top Categories / Order Statistics ── */}
        <div className="row dashboard-section">
          {/* Top Customers */}
          <div className="col-xxl-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-orange fs-16 me-2">
                    <i className="ti ti-users" />
                  </span>
                  <h5 className="card-title mb-0">{t("Top Customers")}</h5>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-calendar me-1" />
                    {periodLabelTC}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setTopCustPeriod("today")}
                      >
                        <span>{window.translate("Today")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setTopCustPeriod("weekly")}
                      >
                        <span>{window.translate("Weekly")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setTopCustPeriod("monthly")}
                      >
                        <span>{window.translate("Monthly")}</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="card-body">
                {renderAccessError(tcErr, { className: "mb-2" })}
                {tcLoading ? (
                  <>
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="d-flex align-items-center justify-content-between border-bottom mb-3 pb-3 flex-wrap gap-2 opacity-50"
                      >
                        <div className="d-flex align-items-center">
                          <span
                            className="avatar avatar-lg bg-light rounded-circle flex-shrink-0"
                            style={{ width: 56, height: 56 }}
                          />
                          <div className="ms-2">
                            <div className="placeholder-glow mb-1">
                              <span className="placeholder col-8" />
                            </div>
                            <div className="placeholder-glow">
                              <span className="placeholder col-6" />
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="placeholder-glow">
                            <span className="placeholder col-4" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                ) : tcItems.length === 0 ? (
                  <div className="text-muted">
                    {t("No customers in this period")}
                  </div>
                ) : (
                  tcItems.map((c, idx) => {
                    const rowClass =
                      idx < tcItems.length - 1 ? "border-bottom mb-3 pb-3" : "";
                    const avatar = resolveImageUrl(c.image);
                    const country = c.country || t("Unknown");
                    const orders = Number(c.orders || 0);
                    const spent = formatMoney(c.totalSpent);
                    return (
                      <div
                        key={String(c.id || idx)}
                        className={`d-flex align-items-center justify-content-between ${rowClass} flex-wrap gap-2`}
                      >
                        <div className="d-flex align-items-center">
                          <Link
                            to="#"
                            className="avatar avatar-lg flex-shrink-0"
                          >
                            <img
                              src={avatar}
                              alt="img"
                              onError={handleImageError}
                            />
                          </Link>
                          <div className="ms-2">
                            <h6 className="fs-14 fw-bold mb-1">
                              <Link to="#">{c.name || "—"}</Link>
                            </h6>
                            <div className="d-flex align-items-center item-list">
                              <p className="d-inline-flex align-items-center mb-0 me-3">
                                <i className="ti ti-map-pin me-1" />
                                {country}
                              </p>
                              <p className="mb-0">
                                {orders.toLocaleString()} {t("Orders")}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="text-end">
                          <h5 className="mb-0">{spent}</h5>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* Top Categories */}
          <div className="col-xxl-4 col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-orange fs-16 me-2">
                    <i className="ti ti-users" />
                  </span>
                  <h5 className="card-title mb-0">{t("Top Categories")}</h5>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-flex align-items-center"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-calendar me-1" />
                    {periodLabelCat}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setCatPeriod("today")}
                      >
                        <span>{window.translate("Today")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setCatPeriod("weekly")}
                      >
                        <span>{window.translate("Weekly")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setCatPeriod("monthly")}
                      >
                        <span>{window.translate("Monthly")}</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="card-body">
                {renderAccessError(topCatErr, { className: "mb-2" })}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-4 mb-4">
                  <div style={{ height: 230, width: 200 }}>
                    {loadingTopCat ? (
                      <div className="d-flex align-items-center justify-content-center w-100 h-100 opacity-50">
                        <div className="spinner-border" role="status">
                          <span className="visually-hidden">
                            {window.translate("Loading…")}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <Doughnut data={doughnutData} options={doughnutOptions} />
                    )}
                  </div>
                  <div>
                    {loadingTopCat ? (
                      <>
                        {[...Array(3)].map((_, i) => (
                          <div key={i} className={i < 2 ? "mb-3" : "mb-0"}>
                            <div className="placeholder-glow">
                              <span className="placeholder col-7" />
                            </div>
                            <div className="placeholder-glow">
                              <span className="placeholder col-5" />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      (topCat.items || []).slice(0, 3).map((c, i) => {
                        const name = c?.name?.en || c?.name?.ar || "—";
                        const sales = Number(c?.sales ?? 0).toLocaleString();
                        const className =
                          i === 0
                            ? "category-item category-primary"
                            : i === 1
                              ? "category-item category-orange"
                              : "category-item category-secondary";
                        return (
                          <div
                            className={className}
                            key={String(c.categoryId || i)}
                          >
                            <p className="fs-13 mb-1">{name}</p>
                            <h2 className="d-flex align-items-center">
                              {sales}
                              <span className="fs-13 fw-normal text-default ms-1">
                                {window.translate("Sales")}
                              </span>
                            </h2>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                <h6 className="mb-2">{t("Category Statistics")}</h6>
                <div className="border br-8">
                  <div className="d-flex align-items-center justify-content-between border-bottom p-2">
                    <p className="d-inline-flex align-items-center mb-0">
                      <i className="ti ti-square-rounded-filled text-indigo fs-8 me-2" />
                      {t("Total Number Of Categories")}
                    </p>
                    <h5>
                      {loadingTopCat
                        ? "—"
                        : Number(topCat?.totalCategories || 0).toLocaleString()}
                    </h5>
                  </div>
                  <div className="d-flex align-items-center justify-content-between p-2">
                    <p className="d-inline-flex align-items-center mb-0">
                      <i className="ti ti-square-rounded-filled text-orange fs-8 me-2" />
                      {t("Total Number Of Products")}
                    </p>
                    <h5>
                      {loadingTopCat
                        ? "—"
                        : Number(topCat?.totalProducts || 0).toLocaleString()}
                    </h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Statistics */}
          <div className="col-xxl-4 col-md-12 d-flex">
            <div className="card flex-fill">
              <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="d-inline-flex align-items-center">
                  <span className="title-icon bg-soft-indigo fs-16 me-2">
                    <i className="ti ti-package" />
                  </span>
                  <h5 className="card-title mb-0">{t("Order Statistics")}</h5>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-calendar me-1" />
                    {label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setPeriod("today")}
                      >
                        <span>{window.translate("Today")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setPeriod("weekly")}
                      >
                        <span>{window.translate("Weekly")}</span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item"
                        style={ddItemStyle}
                        onClick={() => setPeriod("monthly")}
                      >
                        <span>{window.translate("Monthly")}</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="card-body pb-0">
                {renderAccessError(err, { className: "mb-2" })}
                <div id="heat_chart" style={{ minHeight: 370 }}>
                  {loading ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: 370 }}
                    >
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      {window.translate("Loading…")}
                    </div>
                  ) : (
                    <ApexCharts
                      options={heatOptions}
                      series={highlightedSeries}
                      type="heatmap"
                      height={370}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="copyright-footer d-flex align-items-center justify-content-between border-top bg-white gap-3 flex-wrap">
        <p className="fs-13 text-gray-9 mb-0">
          {t("2026 © Hamilton POS. All Right Reserved")}
        </p>
        <p>
          {window.translate("Designed & Developed By")}{" "}
          <Link to="#" className="link-primary">
            {window.translate("Hamilton")}
          </Link>
        </p>
      </div>
    </div>
  );
};

NewDashboard.propTypes = {
  storeId: PropTypes.string,
  warehouseId: PropTypes.string,
};

NewDashboard.defaultProps = {
  storeId: undefined,
  warehouseId: undefined,
};

export default NewDashboard;
